import React from 'react';
import { Card, CardBody, Flex, IconButton, Text, useToast } from '@chakra-ui/react';
import { NotificationType, SystemTranslationKeys } from '@common/entities';
import { NotificationStatus } from 'enums/notification-status';
import {
  dequeueNotification,
  ScheduledChargeNotificationMetadata,
} from 'state/slices/notificationsSlice';
import { useDispatch } from 'state/storeHooks';

import { Caution, ErrorIcon, InfoIcon, SuccessIcon, XIcon } from 'components/custom-icons';
import { useNotificationDrawer } from 'hooks/useNotificationDrawer';
import { useSystemTranslation } from 'hooks/useSystemTranslation';
import { formatDateTime } from 'utils/dateFormatter';
import {
  applyKnownPlaceholders,
  resolveChargeAmounts,
  resolveChargeMetadata,
} from 'utils/scheduledChargeNotificationTranslation';

export interface NotificationCardProps {
  id: string;
  status?: NotificationStatus;
  title: string;
  message?: string;
  date?: string | Date;
  compact?: boolean;
  read?: boolean;
  onClose?: () => void;
  clearAllNotifications?: () => void;
  handleMarkAsRead?: (ids: string[]) => Promise<void>;
  /** Notification type from API (e.g. ScheduledChargeChange) for translation */
  type?: string;
  /** Metadata for client-side translation (API may store nested at .metadata) */
  metadata?: { metadata?: ScheduledChargeNotificationMetadata } & Record<string, unknown>;
}

const statusIconMap = {
  [NotificationStatus.ERROR]: <ErrorIcon data-testid="error-icon" color="red.500" boxSize={4} />,
  [NotificationStatus.WARNING]: (
    <Caution data-testid="caution-icon" color="yellow.700" boxSize={4} />
  ),
  [NotificationStatus.INFO]: <InfoIcon data-testid="info-icon" color="blue.500" boxSize={4} />,
  [NotificationStatus.SUCCESS]: (
    <SuccessIcon data-testid="success-icon" color="green.500" boxSize={4} />
  ),
};

function getTranslatedChargeNotification(
  tSystem: (key: string, opts?: Record<string, unknown>) => string,
  type: string,
  metadata?: { metadata?: ScheduledChargeNotificationMetadata } & Record<string, unknown>,
): { title: string; message: string } | null {
  const isChargeChange =
    type === NotificationType.ScheduledChargeChange || type === 'ScheduledChargeChange';
  if (!isChargeChange || !metadata) {
    return null;
  }
  // API stores full ScheduledChargeNotification in metadata (fields under .metadata); support flat shape too
  const m = resolveChargeMetadata(metadata);
  const chargeName = m?.chargeName ?? '';
  const { previousAmount, currentAmount } = resolveChargeAmounts(m);
  const isIncrease = m?.amountDifference?.isIncrease;
  const hasAmountChange = m?.hasAmountChange;

  // Property/system overrides may use {{previousAmount}}/{{currentAmount}} or {{oldValue}}/{{newValue}}
  const amountInterpolation = {
    previousAmount,
    currentAmount,
    oldValue: previousAmount,
    newValue: currentAmount,
  };

  if (hasAmountChange && previousAmount && currentAmount && isIncrease !== undefined) {
    const titleKey = isIncrease
      ? SystemTranslationKeys.notification.billing.chargeIncreased.title
      : SystemTranslationKeys.notification.billing.chargeDecreased.title;
    const messageKey = isIncrease
      ? SystemTranslationKeys.notification.billing.chargeIncreased.message
      : SystemTranslationKeys.notification.billing.chargeDecreased.message;
    const titleVars = { chargeName };
    const messageVars = { chargeName, ...amountInterpolation };
    return {
      title: applyKnownPlaceholders(tSystem(titleKey, titleVars), titleVars),
      message: applyKnownPlaceholders(tSystem(messageKey, messageVars), messageVars),
    };
  }
  if (chargeName) {
    const titleVars = { chargeName };
    const messageVars = { chargeName };
    return {
      title: applyKnownPlaceholders(
        tSystem(SystemTranslationKeys.notification.billing.chargeUpdated.title, titleVars),
        titleVars,
      ),
      message: applyKnownPlaceholders(
        tSystem(SystemTranslationKeys.notification.billing.chargeUpdated.message, messageVars),
        messageVars,
      ),
    };
  }
  return null;
}

export default function NotificationCard({
  id,
  status,
  title,
  message,
  date,
  compact = false,
  read = false,
  onClose,
  clearAllNotifications,
  handleMarkAsRead,
  type,
  metadata,
}: NotificationCardProps) {
  const icon = status ? statusIconMap[status] : null;
  const dispatch = useDispatch();
  const { onOpen } = useNotificationDrawer();
  const { tSystem } = useSystemTranslation();
  const toast = useToast();

  const translated = getTranslatedChargeNotification(tSystem, type ?? '', metadata);
  const displayTitle = translated?.title ?? title;
  const displayMessage = translated?.message ?? message;

  const handleClose = () => {
    if (compact) {
      toast.close(id);
    } else {
      dispatch(dequeueNotification(id));
    }

    onClose?.();
  };

  const handleNotificationMarkAsRead = async () => {
    if (read) return;
    if (handleMarkAsRead) {
      await handleMarkAsRead([id]);
    }
  };

  return (
    <Card
      role="article"
      p={4}
      boxShadow="md"
      letterSpacing=".25px"
      bg={read ? 'gray.100' : 'white'}
      overflow="visible"
      w="100%"
    >
      <Flex direction="column">
        <Flex align="center" justify="space-between" mb={2} gap={2}>
          <Flex align="center" flex="1" minW={0} gap={3}>
            {icon}
            <Text
              fontWeight="500"
              fontSize="md"
              lineHeight="1.5"
              textTransform="capitalize"
              whiteSpace="pre-wrap"
              wordBreak="break-word"
            >
              {displayTitle}
            </Text>
          </Flex>
          {compact && (
            <IconButton
              aria-label="Clear Notification"
              icon={<XIcon boxSize={2.5} color="gray.700" />}
              onClick={handleClose}
              variant="ghost"
              size="sm"
              flexShrink={0}
              _hover={{ bg: 'transparent' }}
            />
          )}
          {!compact && read && (
            <IconButton
              aria-label="Remove Notification"
              icon={<XIcon boxSize={2.5} color="gray.700" />}
              onClick={handleClose}
              variant="ghost"
              size="sm"
              flexShrink={0}
              _hover={{ bg: 'transparent' }}
            />
          )}
          {!compact && !read && (
            <Text
              fontSize="sm"
              color="blue.500"
              fontWeight="medium"
              cursor="pointer"
              onClick={handleNotificationMarkAsRead}
              flexShrink={0}
            >
              Clear
            </Text>
          )}
        </Flex>
        <CardBody p={0} pl={7}>
          {compact ? (
            <Text
              fontSize="sm"
              color="blue.500"
              fontWeight="medium"
              cursor="pointer"
              onClick={() => {
                onOpen();
                clearAllNotifications?.();
              }}
            >
              View Details
            </Text>
          ) : (
            <>
              {displayMessage && (
                <Text fontSize="sm" mb={1} whiteSpace="pre-wrap" wordBreak="break-word">
                  {displayMessage}
                </Text>
              )}
              {date && <Text fontSize="xs">{formatDateTime(date)}</Text>}
            </>
          )}
        </CardBody>
      </Flex>
    </Card>
  );
}
